/**
 * describeActivity — the three things a notification has to answer: WHO did it,
 * WHAT they did, and WHICH thing it happened to.
 *
 * Both notification panels previously showed only the free-text description and
 * dug the asset out of it with a regex (`/['"]?([A-Z-]{1,}-\d+)['"]?/g`), which
 * meant an activity whose wording lacked a display id had nothing to open —
 * even though resource_id is populated on every row. Worse, when the resource
 * was NOT a work order the old code fell back to the activity's OWN id and
 * tried to open that as a work order.
 *
 * Deriving all three from structured fields, in one place, keeps web and mobile
 * saying the same thing about the same row.
 */
import { formatActivityType } from '../types/activity';
/** Resource types a client can navigate to, given the right handler. */
export const ROUTABLE_RESOURCE_TYPES = [
    'work_order',
    'work_order_service',
    'user',
    'location',
    'account',
    'service',
    'invoice_batch',
];
/**
 * "work_session" -> "Work session". Used when the server could not resolve a
 * name for the resource.
 *
 * This is deliberately NOT a truncated uuid. A row that read "1b7986af…" told
 * the reader nothing — it is not a name, not searchable, and not something
 * anyone can act on. About a third of the activity types have no natural name
 * at all (work sessions, invitations, exports, chats), so the honest label is
 * WHAT the thing was, not a fragment of its primary key.
 */
export function humanizeResourceType(resourceType) {
    const words = resourceType.replace(/_/g, ' ').trim();
    if (!words)
        return 'Item';
    return words.charAt(0).toUpperCase() + words.slice(1);
}
export function describeActivity(activity) {
    const actorName = (activity.actor_name || '').trim();
    const resourceType = (activity.resource_type || '').trim();
    const resourceId = (activity.resource_id || '').trim();
    const resourceName = (activity.resource_name || '').trim();
    return {
        actor: actorName || 'Unknown',
        actorKnown: actorName.length > 0,
        action: formatActivityType(activity.activity_type || ''),
        asset: resourceType && resourceId
            ? {
                type: resourceType,
                id: resourceId,
                label: resourceName || humanizeResourceType(resourceType),
                labelResolved: resourceName.length > 0,
                routable: ROUTABLE_RESOURCE_TYPES.includes(resourceType),
            }
            : null,
    };
}
